'use client';

import { createContext, useMemo, useSyncExternalStore } from 'react';
import type { userAgent } from 'next/server';

/**
 * Narrow is everything below Tailwind's `md` (`min-width: 768px`).
 * The fractional bound closes the gap that `max-width: 767px`
 * leaves open at fractional viewport widths.
 *
 * Width-only by design: viewport HEIGHT changes constantly on
 * mobile — Chrome collapses its URL bar on scroll, the on-screen
 * keyboard opens over the page — and a query carrying a height
 * term would resolve anew on every one of those.
 * Docs:
 *   https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
 */
const NARROW_VIEWPORT_QUERY = '(max-width: 767.98px)';
type Device = ReturnType<typeof userAgent>['device'];

/**
 * Device context providing mobile detection to the component tree.
 */
export const DeviceContext = createContext({
  isMobile: false
});

// Module-scoped so the identity stays stable across renders; a
// fresh `subscribe` each render tears down and re-adds the
// listener every time.
function subscribe(onStoreChange: () => void) {
  const mql = window.matchMedia(NARROW_VIEWPORT_QUERY);
  mql.addEventListener('change', onStoreChange);
  return () => mql.removeEventListener('change', onStoreChange);
}
function getSnapshot() {
  return window.matchMedia(NARROW_VIEWPORT_QUERY).matches;
}

/**
 * Viewport width is the single source of truth for `isMobile`: the
 * media query notifies only when the 768px boundary is actually
 * crossed, so `isMobile` holds steady through scrolling, keyboard
 * entry, and every other height-only viewport change.
 *
 * That stability is load-bearing. Consumers gate element mounts on
 * `isMobile` — the signing view's overlay-control buttons and right
 * panel among them — so each change unmounts and remounts real
 * subtrees. Wrap at the root layout; pass `device` only in tests
 * to seed the server snapshot.
 */
export function DeviceProvider({
  children,
  device
}: {
  children: React.ReactNode;
  device?: Device;
}) {
  const isMobile = useSyncExternalStore(subscribe, getSnapshot,
  // Server render has no viewport, so a phone UA stands in for a
  // narrow one. Tablets seed wide, matching what the query
  // resolves to on all but the narrowest. A seed that disagrees
  // with the client costs one corrective re-render at hydration
  // and nothing after.
  () => device?.type === 'mobile');
  const value = useMemo(() => ({
    isMobile
  }), [isMobile]);
  return <DeviceContext.Provider value={value} data-sentry-element="DeviceContext.Provider" data-sentry-component="DeviceProvider" data-sentry-source-file="device-provider.tsx">{children}</DeviceContext.Provider>;
}